// Toast Component - Sistema de notificaciones
import { useState, useEffect, useCallback } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { Toast } from './use-toast';

interface ToastItemProps {
  toast: Toast;
  onClose: (id: string) => void;
}

function ToastItem({ toast, onClose }: ToastItemProps) {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Duración por defecto según el tipo de toast
    const defaultDuration = {
      success: 5000,  // 5 segundos para éxito
      error: 12000,   // 12 segundos para errores (más tiempo para leer)
      warning: 6000,  // 6 segundos para advertencias
      info: 5000,     // 5 segundos para info
    };

    const duration = toast.duration || defaultDuration[toast.type] || 5000;

    const timer = setTimeout(() => {
      setIsExiting(true);
      // Esperar a que termine la animación antes de remover
      setTimeout(() => {
        onClose(toast.id);
      }, 300);
    }, duration);

    return () => clearTimeout(timer);
  // H-F051: toast.type incluido en deps para recalcular duracion si React reutiliza el componente
  }, [toast.id, toast.type, toast.duration, onClose]);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onClose(toast.id);
    }, 300);
  }, [toast.id, onClose]);

  const icons = {
    success: CheckCircle,
    error: AlertCircle,
    info: Info,
    warning: AlertTriangle,
  };

  const styles = {
    success: 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/30 dark:border-green-800 dark:text-green-300',
    error: 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/30 dark:border-red-800 dark:text-red-300',
    info: 'bg-teal-50 border-teal-200 text-teal-800 dark:bg-teal-900/30 dark:border-teal-800 dark:text-teal-300',
    warning: 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/30 dark:border-amber-800 dark:text-amber-300',
  };

  const Icon = icons[toast.type];

  return (
    <div
      role={toast.type === 'error' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-3 p-4 rounded-lg border shadow-lg min-w-0 w-full sm:min-w-[300px] max-w-md transition-all duration-300 ease-in-out',
        styles[toast.type],
        isExiting && 'opacity-0 translate-x-full'
      )}
      style={{
        animation: isExiting ? 'none' : 'slideIn 0.3s ease-out',
      }}
    >
      <Icon className="h-5 w-5 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm font-medium">{toast.message}</p>
      </div>
      <button
        onClick={handleClose}
        className="flex-shrink-0 hover:opacity-70 transition-opacity"
        aria-label="Cerrar notificación"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// Toast Container
export function ToastContainer({ toasts, onClose }: { toasts: Toast[]; onClose: (id: string) => void }) {
  if (toasts.length === 0) return null;

  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="fixed top-4 right-4 left-4 sm:left-auto sm:w-auto z-50 flex flex-col gap-2"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onClose={onClose} />
      ))}
    </div>
  );
}
